import { type RecommendationProduct } from '@/common/types/recommended-products';
import { useQuery } from '@tanstack/react-query';
import Client from '@/common/utils/client.utility';
import { createQueryStringUtility } from '@/common/utils/create-query-string.utility';

export interface RecommendationQueryParams {
  productId: number;
  vendorId?: number;
}

export type RecommendationStrategy = 'CategoryBased' | 'VendorBased';

export interface StrategyRecommendationQueryParams {
  productId: number;
  strategy: RecommendationStrategy;
  count?: number;
}

export interface RecommendationResponse {
  data: {
    recommendedProducts: RecommendationProduct[] | null;
    strategy?: string;
  };
}

export interface WitroshopRecommendationResponse {
  recommendedProducts?: RecommendationProduct[] | null;
  strategy?: string;
  data?: RecommendationResponse['data'] | null;
}

export type ProductItemType = (
  params: RecommendationQueryParams,
  disabelToast?: boolean
) => Promise<RecommendationResponse>;

const getRecommendedProduct: ProductItemType = async (params, disableToast) => {
  const client = new Client(disableToast);

  const endpoint = `/v1/ProductRecommendation/item-to-item${createQueryStringUtility(
    {
      productId: params.productId,
      vendorId: params.vendorId,
    }
  )}`;
  return await client.get(endpoint);
};

export const getStrategyRecommendedProduct = async (
  params: StrategyRecommendationQueryParams,
  disableToast?: boolean
): Promise<RecommendationResponse> => {
  const client = new Client(disableToast);
  const endpoint = `/v1/ProductRecommendation/recommendations/${params.productId}${createQueryStringUtility(
    {
      strategy: params.strategy,
      count: params.count ?? 20,
    }
  )}`;

  return await client.get(endpoint);
};

const getUserRecommendedProducts = async (
  vendorId: number
): Promise<RecommendationResponse> => {
  const client = new Client(true);
  const endpoint = `/v1/ProductRecommendation/user-to-item${createQueryStringUtility(
    { vendorId }
  )}`;

  return await client.get(endpoint);
};

export function useGetRecommendedProducts({
  enabled,
  query,
  disableToast = false,
}: {
  enabled?: boolean;
  query: RecommendationQueryParams;
  disableToast?: boolean;
}) {
  return useQuery<RecommendationResponse, Error>({
    queryFn: async () => await getRecommendedProduct(query, disableToast),
    queryKey: ['products-recommendations', query],
    retry: 0,
    enabled,
  });
}

export function useGetStrategyRecommendedProducts({
  enabled,
  query,
  disableToast = false,
}: {
  enabled?: boolean;
  query: StrategyRecommendationQueryParams;
  disableToast?: boolean;
}) {
  return useQuery<RecommendationResponse, Error>({
    queryFn: async () =>
      await getStrategyRecommendedProduct(query, disableToast),
    queryKey: ['products-strategy-recommendations', query],
    retry: 0,
    enabled,
  });
}

export function useGetUserRecommendedProducts({
  enabled = true,
  vendorId,
}: {
  enabled?: boolean;
  vendorId: number;
}) {
  return useQuery<RecommendationResponse, Error>({
    queryFn: async () => await getUserRecommendedProducts(vendorId),
    queryKey: ['store-user-recommended-products', vendorId],
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled && Boolean(vendorId),
  });
}

const getWitroshopUserRecommendedProducts =
  async (): Promise<WitroshopRecommendationResponse> => {
    const client = new Client(true);
    return await client.get('/v1/ProductRecommendation/witroshop-user-to-item');
  };

export function useGetWitroshopUserRecommendedProducts({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) {
  return useQuery<WitroshopRecommendationResponse, Error>({
    queryFn: getWitroshopUserRecommendedProducts,
    queryKey: ['witroshop-user-to-item-recommendations'],
    retry: false,
    refetchOnWindowFocus: false,
    enabled,
  });
}
